import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { config } from '../config/env';
import {
  BUILTIN_VIEWER,
  BUILTIN_VIEWER_ID,
  parseViewers,
  primaryViewerUrl,
  viewerOptions,
  type ConfiguredViewer,
  type Viewer,
  type ViewerTarget,
} from '../utils/viewers';

interface ViewersState {
  viewers: Viewer[];
  loaded: boolean;
}

const listeners = new Set<() => void>();
let viewersState: ViewersState = { viewers: [BUILTIN_VIEWER], loaded: false };
let fetchStarted = false;

function emit() {
  for (const listener of listeners) listener();
}

const CONFIG_TIMEOUT_MS = 5000;
const RETRY_INITIAL_MS = 30_000;
const RETRY_MAX_MS = 300_000;

/** Null when the request fails, so the caller can retry. */
async function fetchConfiguredViewers(): Promise<ConfiguredViewer[] | null> {
  try {
    const response = await fetch(`${config.apiBaseUrl}/auth/config`, {
      signal: AbortSignal.timeout(CONFIG_TIMEOUT_MS),
    });
    if (!response.ok) return null;
    const data: unknown = await response.json();
    if (typeof data !== 'object' || data === null) return null;
    return parseViewers((data as { viewers?: unknown }).viewers);
  } catch {
    return null;
  }
}

function loadViewers(retryMs: number) {
  void fetchConfiguredViewers().then(configured => {
    const viewers = configured?.length
      ? [BUILTIN_VIEWER, ...configured]
      : viewersState.viewers;
    if (!viewersState.loaded || viewers !== viewersState.viewers) {
      viewersState = { viewers, loaded: true };
      emit();
    }
    if (configured !== null) return;
    setTimeout(() => {
      if (listeners.size) loadViewers(Math.min(retryMs * 2, RETRY_MAX_MS));
      else fetchStarted = false;
    }, retryMs);
  });
}

function subscribeViewers(listener: () => void) {
  listeners.add(listener);
  if (!fetchStarted) {
    fetchStarted = true;
    loadViewers(RETRY_INITIAL_MS);
  }
  return () => {
    listeners.delete(listener);
  };
}

const getViewersState = () => viewersState;

const DEFAULT_VIEWER_KEY = 'hawk:default-viewer';

function readDefaultViewerId(): string {
  try {
    return localStorage.getItem(DEFAULT_VIEWER_KEY) || BUILTIN_VIEWER_ID;
  } catch {
    return BUILTIN_VIEWER_ID;
  }
}

let defaultViewerId: string | null = null;

function subscribeDefault(listener: () => void) {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key !== DEFAULT_VIEWER_KEY) return;
    defaultViewerId = readDefaultViewerId();
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

const getDefaultViewerId = () => (defaultViewerId ??= readDefaultViewerId());

export function setDefaultViewerId(id: string): void {
  defaultViewerId = id;
  try {
    localStorage.setItem(DEFAULT_VIEWER_KEY, id);
  } catch {
    // ignore: the choice lasts until reload
  }
  emit();
}

export interface UseViewersResult {
  viewers: Viewer[];
  loaded: boolean;
  defaultViewerId: string;
  setDefaultViewerId: (id: string) => void;
  primaryUrl: (
    target: ViewerTarget,
    options?: { redirect?: boolean }
  ) => string;
  options: (target: ViewerTarget) => ReturnType<typeof viewerOptions>;
}

export function useViewers(): UseViewersResult {
  const { viewers, loaded } = useSyncExternalStore(
    subscribeViewers,
    getViewersState
  );
  const defaultId = useSyncExternalStore(subscribeDefault, getDefaultViewerId);
  const primaryUrl = useCallback(
    (target: ViewerTarget, options?: { redirect?: boolean }) =>
      primaryViewerUrl(viewers, defaultId, target, options),
    [viewers, defaultId]
  );
  const options = useCallback(
    (target: ViewerTarget) => viewerOptions(viewers, defaultId, target),
    [viewers, defaultId]
  );
  return useMemo(
    () => ({
      viewers,
      loaded,
      defaultViewerId: defaultId,
      setDefaultViewerId,
      primaryUrl,
      options,
    }),
    [viewers, loaded, defaultId, primaryUrl, options]
  );
}
